import AdapterDateFns from '@mui/lab/AdapterDateFns';
import DatePicker from '@mui/lab/DatePicker';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import TimePicker from '@mui/lab/TimePicker';
import {
  Box,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { GenDate } from '@pinnacleautomotive/clarity-components';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { NewOrderTypes } from '../../../../common/enums/enums';
import GenAlert from '../../../../components/GenAlert';
import { getSelectorActionsBasedOnType } from '../helper';

export function onChangePromiseDateWrapper(
  setIsInvalidDate,
  promiseDate,
  dispatch,
  actions
) {
  return event => {
    if (!event || isNaN(event.getTime())) {
      setIsInvalidDate(true);
      return;
    }

    setIsInvalidDate(false);
    const newDate = new Date(event);
    newDate.setHours(promiseDate.getHours());
    newDate.setMinutes(promiseDate.getMinutes());

    dispatch(actions(newDate.getTime()));
  };
}

export function onChangePromiseTimeWrapper(
  setIsInvalidTime,
  promiseDate,
  dispatch,
  actions
) {
  return event => {
    if (!event || isNaN(event.getTime())) {
      setIsInvalidTime(true);
      return;
    }

    setIsInvalidTime(false);
    const newDate = new Date(promiseDate);
    newDate.setHours(event.getHours());
    newDate.setMinutes(event.getMinutes());
    dispatch(actions(newDate.getTime()));
  };
}

export default function CompletionTime({
  onValidStep,
  boxProps = { p: 4, px: 8 },
  orderType = NewOrderTypes.SIMPLE_ORDER,
}) {
  const { t } = useTranslation('orders');
  const dispatch = useDispatch();
  const {
    changeCompletionTypeAction,
    setPromiseDateAction,
    updateCreatedDateAction,
    estimatedCompletionSelector,
    completionTypeSelector,
    promiseDateSelector,
    selectedPricesSelector,
  } = getSelectorActionsBasedOnType(orderType);
  const completionType = useSelector(completionTypeSelector);
  const estimatedCompletion = useSelector(estimatedCompletionSelector);
  const promiseDate = useSelector(promiseDateSelector);
  const selectedPrices = useSelector(selectedPricesSelector) || [];
  const [isInvalidDate, setIsInvalidDate] = useState(false);
  const [isInvalidTime, setIsInvalidTime] = useState(false);
  const [showInvalidComplationDate, setShowInvalidComplationDate] =
    useState(false);

  const changeCompletionTypeHandler = event => {
    dispatch(changeCompletionTypeAction(event.target.value));
  };

  const onChangePromiseDate = onChangePromiseDateWrapper(
    setIsInvalidDate,
    promiseDate,
    dispatch,
    setPromiseDateAction
  );

  const onChangePromiseTime = onChangePromiseTimeWrapper(
    setIsInvalidTime,
    promiseDate,
    dispatch,
    setPromiseDateAction
  );

  const disabled = !selectedPrices.length;

  useEffect(() => {
    completionType === 'promised' &&
      window.scrollTo({ top: document.body.scrollHeight });
  }, [completionType]);

  useEffect(() => {
    dispatch(updateCreatedDateAction());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    if (
      completionType === 'promised' &&
      promiseDate.getTime() < estimatedCompletion.toJSDate().getTime()
    ) {
      setShowInvalidComplationDate(true);
      onValidStep(false);
      return;
    }

    setShowInvalidComplationDate(false);
    onValidStep(!isInvalidDate && !isInvalidTime);
  }, [
    onValidStep,
    promiseDate,
    estimatedCompletion,
    completionType,
    isInvalidDate,
    isInvalidTime,
  ]);

  return (
    <Box {...boxProps}>
      <Box mb={1}>
        <Typography variant="h6">
          {t('newOrder.completionTime.title')}
        </Typography>
      </Box>
      <Box px={1} py={3}>
        <RadioGroup
          row
          sx={{ gap: 4 }}
          value={completionType}
          onChange={changeCompletionTypeHandler}
        >
          <FormControlLabel
            control={<Radio />}
            disabled={disabled}
            value="estimated"
            label={
              <Stack>
                <Typography variant="body1">
                  {t('newOrder.completionTime.estimatedCompletionDate')}
                </Typography>

                <Typography variant="caption">
                  {(disabled && t('newOrder.selectServicesMessage')) || (
                    <GenDate date={estimatedCompletion} />
                  )}
                </Typography>
              </Stack>
            }
          />
          <FormControlLabel
            control={<Radio />}
            value="promised"
            disabled={disabled}
            label={
              <Stack>
                <Typography variant="body1">
                  {t('newOrder.completionTime.promisedTime')}
                </Typography>
                <Typography variant="caption">
                  {t('newOrder.completionTime.promisedTimeCaption')}
                </Typography>
              </Stack>
            }
          />
        </RadioGroup>
      </Box>
      {completionType === 'promised' && (
        <>
          <Divider />
          <Stack direction="row" px={1} py={3} gap={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Date"
                value={promiseDate}
                disablePast={true}
                onChange={onChangePromiseDate}
                renderInput={params => (
                  <TextField
                    {...params}
                    error={params.error || isInvalidDate}
                    size="small"
                    data-testid="dateInput"
                    sx={{ background: theme => theme.palette.background.paper }}
                  />
                )}
              />
              <TimePicker
                label="Time"
                value={promiseDate}
                onChange={onChangePromiseTime}
                renderInput={params => (
                  <TextField
                    {...params}
                    error={params.error || isInvalidTime}
                    size="small"
                    sx={{ background: theme => theme.palette.background.paper }}
                  />
                )}
              />
            </LocalizationProvider>
          </Stack>
        </>
      )}
      <GenAlert open={showInvalidComplationDate} severity="error">
        {t('newOrder.completionTime.promisedTimeError')}
      </GenAlert>
    </Box>
  );
}
